import { RestClient } from "./restClient";

/** What a company's turns will run on, and where that came from. */
export interface LlmSetting {
  /** Provider id, e.g. "anthropic" | "fireworks". Null when unset. */
  provider: string | null;
  /** Model id in effect. Null when neither the company nor the platform has
   *  chosen — chat then uses its own construction-time default. */
  model: string | null;
  temperature: number | null;
  /** "company" when this company chose it, "platform" when it is inheriting
   *  the deployment default, "default" when nothing is set anywhere.
   *
   *  Computed by the SERVER, deliberately. Comparing the company value against
   *  the platform default client-side would mislabel a company that chose the
   *  same value on purpose — that company is overridden, and must not start
   *  following the default when it changes.
   */
  source: "company" | "platform" | "default";
  /** Valid values, served by the API so the dropdowns cannot drift from what
   *  the gateway actually serves — the picker must not offer a model chat
   *  would then reject. */
  options: { providers: string[]; models: string[] };
}

/** A partial update. `null` CLEARS a field so the company inherits again;
 *  omitting a field leaves it untouched. Those are different requests, which
 *  is why this is not simply `Partial<LlmSetting>`. */
export interface LlmSettingUpdate {
  provider?: string | null;
  model?: string | null;
  temperature?: number | null;
}

/** Session-scoped cache, module scope rather than component state: the
 *  settings panel unmounts on every tab switch in Organization settings, so a
 *  plain `useEffect` fetch would refire on each click. Mirrors
 *  kbSettingsApi's cache for the same reason. */
let cached: LlmSetting | null = null;
let cachedDefault: LlmSetting | null = null;

/** Drop the cached values so the next load refetches. */
export function clearLlmSettingCache(): void {
  cached = null;
  cachedDefault = null;
}

/** Wraps /v1/settings/llm. Methods throw RestError on non-2xx — notably 403
 *  for a non-admin, which the panel renders rather than swallowing: a silent
 *  empty panel is indistinguishable from a broken one. */
export class LlmSettingsApi {
  constructor(private client: RestClient) {}

  /** The company's in-effect choice, from cache when already fetched. */
  async load(): Promise<LlmSetting> {
    if (cached) return cached;
    cached = await this.client.get<LlmSetting>("/v1/settings/llm");
    return cached;
  }

  /** Change (or clear) the company's choice.
   *
   *  The RESPONSE becomes the cache, not the submitted patch: the server owns
   *  normalisation and recomputes `source`, which flips to "platform" the
   *  moment a field is cleared.
   */
  async save(patch: LlmSettingUpdate): Promise<LlmSetting> {
    const saved = await this.client.put<LlmSetting>("/v1/settings/llm", patch);
    cached = saved;
    return saved;
  }

  /** The deployment-wide default. Platform admins only for writes. */
  async loadDefault(): Promise<LlmSetting> {
    if (cachedDefault) return cachedDefault;
    cachedDefault = await this.client.get<LlmSetting>("/v1/settings/llm/default");
    return cachedDefault;
  }

  /** Set the deployment-wide default. Companies with an explicit choice are
   *  unaffected — clearing the cache for the company view too, because what
   *  an inheriting company resolves to has just changed. */
  async saveDefault(patch: LlmSettingUpdate): Promise<LlmSetting> {
    const saved = await this.client.put<LlmSetting>(
      "/v1/settings/llm/default",
      patch,
    );
    cachedDefault = saved;
    cached = null;
    return saved;
  }
}
